import type { ReactNode } from "react";
import { Link } from "react-router";
import styles from "./Button.module.css";
import { Icon, type IconName } from "./icons";

type Variant = "primary" | "secondary" | "text" | "danger";

type ButtonProps = {
  children: ReactNode;
  variant?: Variant;
  fullWidth?: boolean;
  compact?: boolean;
  // Действие выполняется: кнопка показывает ожидание и не нажимается.
  busy?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
  // Ссылка внутри сайта — вид кнопки, поведение ссылки.
  to?: string;
  // Скачать файл по внешней ссылке (временная ссылка хранилища).
  download?: string;
  icon?: IconName;
};

function classNames(...names: (string | false | undefined)[]): string {
  return names.filter(Boolean).join(" ");
}

export function Button({
  children,
  variant = "primary",
  fullWidth = true,
  compact = false,
  busy = false,
  disabled = false,
  onClick,
  type = "button",
  to,
  download,
  icon,
}: ButtonProps) {
  const className = classNames(
    styles.button,
    styles[variant],
    fullWidth && variant !== "text" && styles.fullWidth,
    compact && styles.compact,
  );
  const content = (
    <>
      {busy && <span className={styles.spinner} aria-hidden="true" />}
      {children}
      {icon !== undefined && !busy && <Icon name={icon} size={18} />}
    </>
  );
  if (download !== undefined) {
    return (
      <a href={download} download className={className}>
        {content}
      </a>
    );
  }
  if (to !== undefined) {
    return (
      <Link to={to} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <button
      type={type}
      className={className}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      {content}
    </button>
  );
}

// Кнопка-иконка: подпись обязательна — без неё экранный диктор прочтёт пустоту.
export function IconButton({
  icon,
  label,
  onClick,
  to,
}: {
  icon: IconName;
  label: string;
  onClick?: () => void;
  to?: string;
}) {
  if (to !== undefined) {
    return (
      <Link to={to} className={styles.iconButton} aria-label={label}>
        <Icon name={icon} size={22} />
      </Link>
    );
  }
  return (
    <button type="button" className={styles.iconButton} aria-label={label} onClick={onClick}>
      <Icon name={icon} size={22} />
    </button>
  );
}
